/**
 * Client-side buyer match re-scoring
 *
 * Re-evaluates the 3 dynamic criteria (priceRange, profitReturns, rehabLevel)
 * when the user edits deal numbers (ARV, repair cost, purchase price).
 * Static criteria (geography, propertyType, characteristics, dealStructure)
 * pass through unchanged from the server-computed breakdown.
 */

// Same thresholds as backend (normalization.js → deriveRehabLevel)
const deriveRehabLevel = (repairCost, squareFeet) => {
    if (!repairCost || repairCost <= 0 || !squareFeet || squareFeet <= 0) return 'turnkey';
    const costPerSqFt = repairCost / squareFeet;
    if (costPerSqFt <= 5) return 'turnkey';
    if (costPerSqFt <= 15) return 'cosmetic';
    if (costPerSqFt <= 30) return 'light_rehab';
    if (costPerSqFt <= 60) return 'value_add';
    return 'gut_rehab';
};

// Same index map as backend (normalization.js → REHAB_LEVEL_INDEX_MAP) — simplified to generic buyer terms
const REHAB_LEVEL_INDEX = {
    turnkey: 0, rent_ready: 0, pristine: 0, fully_renovated: 0,
    cosmetic: 1, lipstick: 1, light: 1, partial_rehab: 1,
    light_rehab: 2, cosmetic_update: 2, full_rehab: 2,
    value_add: 3, moderate_overhaul: 3, medium: 3, full_rehab_exterior_cosmetics: 3,
    heavy_rehab: 4, structural: 4, full_renovation: 4, full_rehab_several_big_ticket_items: 4,
    gut_rehab: 5, teardown: 5, complete_rebuild: 5, heavy: 5, fire_damaged: 5,
    level_0: 0, level_1: 1, level_2: 2, level_3: 3, level_4: 4, level_5: 5,
};

const getRehabIndex = (level) => {
    if (!level) return null;
    // Normalize: backend stores some levels with spaces (e.g. 'cosmetic update'), map uses underscores
    const normalized = level.toLowerCase().trim().replace(/\s+/g, '_');
    const idx = REHAB_LEVEL_INDEX[normalized];
    return idx !== undefined ? idx : null;
};

/**
 * Re-score the 3 dynamic criteria based on user-edited property values.
 * Returns an updated breakdown (shallow clone — static criteria are references).
 *
 * @param {Object} breakdown - The server-computed enriched breakdown from the match
 * @param {Object} opts
 * @param {number|null} opts.purchasePrice - User-edited purchase price
 * @param {number|null} opts.arv - User-edited ARV
 * @param {number|null} opts.repairCost - User-edited repair cost
 * @param {number|null} opts.squareFeet - From lead (static)
 * @param {Object|null} opts.profitResult - From calculateProfit() with user-edited values
 * @returns {Object} Updated breakdown with re-scored met/propertyValue for dynamic criteria
 */
export const rescoreDynamicCriteria = (breakdown, { purchasePrice, arv, repairCost, squareFeet, profitResult }) => {
    if (!breakdown) return breakdown;

    // Shallow-clone so we don't mutate the original
    const updated = { ...breakdown };

    // --- Price Range ---
    // Skip re-scoring for portfolio deals — portfolio price is the combined asking price
    // from the portfolio document, not the individual lead's purchase price
    if (updated.priceRange?.active && updated.priceRange.buyerCriteria && updated.priceRange.label !== 'Portfolio Price') {
        const bc = updated.priceRange.buyerCriteria;
        const meetsMin = bc.min == null || (purchasePrice != null && purchasePrice >= bc.min);
        const meetsMax = bc.max == null || (purchasePrice != null && purchasePrice <= bc.max);
        updated.priceRange = {
            ...updated.priceRange,
            met: meetsMin && meetsMax,
            propertyValue: purchasePrice,
        };
    }

    // --- Profit / Returns ---
    // Co-living deals score profitReturns on co-living metrics (cap rate / cash flow /
    // etc.), not the flip profit fields re-computed below. A co-living breakdown carries
    // coLiving-prefixed propertyValue keys — skip flip re-scoring for it (the backend
    // values are authoritative and this frontend calc has no co-living inputs). Guards
    // the hybrid-deal case where a flip profitResult is present on a co-living breakdown.
    const isCoLivingReturns = updated.profitReturns?.propertyValue
        && 'coLivingCapRate' in updated.profitReturns.propertyValue;
    if (updated.profitReturns?.active && updated.profitReturns.buyerCriteria && profitResult && !isCoLivingReturns) {
        const bc = updated.profitReturns.buyerCriteria;
        const profit = profitResult?.estimatedProfit;
        // Keep estimatedProfitPercentage in same scale as backend (percentage form, e.g. 21.4 = 21.4%)
        const profitPct = profitResult?.estimatedProfitPercentage ?? null;
        const pctOfARV = (arv && arv > 0 && purchasePrice != null)
            ? (purchasePrice / arv) * 100
            : null;

        // Re-evaluate the 3 sub-thresholds we CAN accurately compute on the frontend.
        // ROI (minRoiPercent) is NOT re-scored because the backend uses a leveraged
        // cash-on-cash formula (calculateROI) that requires pointsRate/downPayment
        // which aren't available here. We preserve the server value for ROI.
        const subThresholds = [
            { criterion: 'minProfitDollars', threshold: bc.minProfitDollars, actual: profit },
            { criterion: 'minProfitPercentage', threshold: bc.minProfitPercentage, actual: profitPct },
            { criterion: 'maxPurchasePricePercentOfARV', threshold: bc.maxPurchasePricePercentOfARV, actual: pctOfARV },
        ];

        const updatedDetails = subThresholds
            .filter(s => s.threshold != null)
            .map(s => {
                const met = s.actual != null && (
                    s.criterion === 'maxPurchasePricePercentOfARV'
                        ? s.actual <= s.threshold
                        : s.actual >= s.threshold
                );
                return {
                    criterion: s.criterion,
                    threshold: s.threshold,
                    actual: s.actual != null ? Math.round(s.actual * 100) / 100 : null,
                    met,
                };
            });

        // Preserve the original server-computed ROI detail if it exists
        const originalDetails = updated.profitReturns.details || [];
        const originalRoi = originalDetails.find(d => d.criterion === 'minRoiPercent');
        if (originalRoi) {
            updatedDetails.push(originalRoi);
        }

        const activeDetails = updatedDetails.filter(d => d.threshold != null);
        const allMet = activeDetails.length > 0 && activeDetails.every(d => d.met);

        updated.profitReturns = {
            ...updated.profitReturns,
            met: allMet,
            propertyValue: {
                ...(updated.profitReturns.propertyValue || {}),
                estimatedProfit: profit,
                estimatedProfitPercentage: profitPct,
                purchasePricePercentOfARV: pctOfARV,
            },
            details: updatedDetails,
        };
    }

    // --- Rehab Level ---
    if (updated.rehabLevel?.active && updated.rehabLevel.buyerCriteria) {
        const buyerLevels = updated.rehabLevel.buyerCriteria.levels || [];
        const newLevel = deriveRehabLevel(repairCost, squareFeet);
        const propIdx = getRehabIndex(newLevel);
        const buyerIndices = buyerLevels.map(l => getRehabIndex(l)).filter(i => i !== null);

        let met = false;
        if (propIdx !== null && buyerIndices.length > 0) {
            const minDist = Math.min(...buyerIndices.map(bi => Math.abs(bi - propIdx)));
            met = minDist <= 1; // exact or adjacent
        }

        updated.rehabLevel = {
            ...updated.rehabLevel,
            met,
            propertyValue: newLevel,
        };
    }

    return updated;
};

/**
 * Compute preference summary from a (possibly re-scored) breakdown.
 * @param {Object} breakdown
 * @returns {{ met: number, total: number }}
 */
export const computePreferenceSummary = (breakdown) => {
    if (!breakdown) return { met: 0, total: 0 };
    const entries = Object.values(breakdown);
    const active = entries.filter(e => e.active);
    const met = active.filter(e => e.met);
    return { met: met.length, total: active.length };
};
